import React, { useEffect } from 'react';
import { Box, Button, Container, Grid, Typography } from '@mui/material';
import './styles.scss';

// ─────────────────────────────────────────────────────────────────────────────
// Static data
// ─────────────────────────────────────────────────────────────────────────────

const recoveryCards = [
  {
    title: 'IEEPA Recovery Kit',
    body: 'Tap into the $166B IEEPA tariff refund pool. We automate the identification and batching of eligible entries for rapid recovery via the ACE/CAPE portal.',
    icon: (
      <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
  },
  {
    title: 'Broker Accountability',
    body: 'Stop paying for systemic HTS misclassifications. We detect when defaults (like generalized parts categories) result in missed exclusions and overpayments.',
    icon: (
      <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
      </svg>
    ),
  },
  {
    title: 'APA Valuation Automation',
    body: 'Real-time monitoring of Related-Party Transactions against Advance Pricing Agreements to ensure valuation accuracy and mitigate retroactive fines.',
    icon: (
      <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
      </svg>
    ),
  },
];

const defenseItems = [
  {
    title: 'The "Ground Truth" Engine:',
    body: 'Document-agnostic ingestion automatically reconciles Source Data (ASNs, Commercial Invoices, Pivot Tables) against CBP Form 7501s.',
  },
  {
    title: 'Reasonable Care Audit Trail:',
    body: 'Generate deterministic, mathematically sound proof of compliance for customs audits.',
  },
  {
    title: 'Managed Services & Human-in-the-Loop:',
    body: 'Strategic expert oversight ensures complex rulings and exceptions are handled perfectly before finalizing data.',
  },
];

const flowSteps = [
  { label: '1. Ingest Source Documents (ASNs, Invoices)', variant: 'step', border: 'blue-light' },
  { label: '↓', variant: 'arrow' },
  { label: '2. Extract via Enterprise AI', variant: 'step', border: 'blue-mid' },
  { label: '↓', variant: 'arrow' },
  { label: '3. Cross-reference against Broker 7501s', variant: 'step', border: 'blue-dark' },
  { label: '↓', variant: 'arrow' },
  { label: '4. Generate Discrepancy & Refund Report', variant: 'step', border: 'green' },
];

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

const RecoveryAndDefensePage = () => {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  return (
    <Box className="rd-page">

      {/* ── Hero ──────────────────────────────────────────────────────────── */}
      <Box className="rd-hero">
        <Container maxWidth="lg">
          <Box className="rd-hero__inner">
            <Typography variant="h1" className="rd-hero__title">
              Turn Compliance into Capital.{' '}
              <Box component="span" className="rd-hero__accent">
                Recover Overpaid Tariffs. Defend Your Supply Chain.
              </Box>
            </Typography>
            <Typography className="rd-hero__subtitle">
              Leverage the Lusid Audit platform to identify systemic broker errors, automate
              IEEPA recovery, and establish an impenetrable 19 CFR "Reasonable Care" audit trail.
            </Typography>
            <Box className="rd-hero__cta-row">
              <Button
                variant="contained"
                className="rd-hero__btn rd-hero__btn--primary"
                component="a"
                href="https://scheduler.zoom.us/rajeev-malhotra/30-min"
                target="_blank"
                rel="noopener noreferrer"
              >
                Start a Recovery Audit
              </Button>
              <Button
                variant="outlined"
                className="rd-hero__btn rd-hero__btn--secondary"
                onClick={() => {
                  const el = document.getElementById('defense');
                  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }}
              >
                Explore Trade Defense
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* ── Phase 1: Recovery ─────────────────────────────────────────────── */}
      <Box id="recovery" className="rd-recovery">
        <Container maxWidth="lg">
          <Box className="rd-recovery__header">
            <Typography variant="h2" className="rd-recovery__title">
              Phase 1: Intelligent Tariff Recovery
            </Typography>
            <Typography className="rd-recovery__subtitle">
              Reclaim what is rightfully yours. We identify the anomalies that manual reviews miss.
            </Typography>
          </Box>
          <Grid container spacing={4}>
            {recoveryCards.map((card) => (
              <Grid item xs={12} md={4} key={card.title}>
                <Box className="rd-card">
                  <Box className="rd-card__icon-wrap">
                    {card.icon}
                  </Box>
                  <Typography variant="h3" className="rd-card__title">
                    {card.title}
                  </Typography>
                  <Typography className="rd-card__body">{card.body}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ── Phase 2: Defense ──────────────────────────────────────────────── */}
      <Box id="defense" className="rd-defense">
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} lg={6}>
              <Typography variant="h2" className="rd-defense__title">
                Phase 2: Continuous Trade Defense
              </Typography>
              <Typography className="rd-defense__body">
                Once we recover your funds, we protect your future. As the Importer of Record, the
                ultimate liability rests with you, not your broker. LusidPay acts as your technology
                infrastructure, providing an automated governance layer.
              </Typography>
              <ul className="rd-defense__list">
                {defenseItems.map((item) => (
                  <li key={item.title} className="rd-defense__list-item">
                    <span className="rd-defense__check">✔</span>
                    <span>
                      <strong>{item.title}</strong> {item.body}
                    </span>
                  </li>
                ))}
              </ul>
            </Grid>
            <Grid item xs={12} lg={6}>
              <Box className="rd-flow">
                <Typography className="rd-flow__label">The Reconciliation Flow</Typography>
                <Box className="rd-flow__steps">
                  {flowSteps.map((step, idx) => (
                    step.variant === 'arrow'
                      ? <Typography key={idx} className="rd-flow__arrow">↓</Typography>
                      : <Box key={idx} className={`rd-flow__step rd-flow__step--${step.border}`}>
                          {step.label}
                        </Box>
                  ))}
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ── CTA Band ──────────────────────────────────────────────────────── */}
      <Box className="rd-cta">
        <Container maxWidth="md">
          <Box className="rd-cta__inner">
            <Typography variant="h2" className="rd-cta__title">
              Ready to see your Ground Truth?
            </Typography>
            <Typography className="rd-cta__body">
              Provide us with 15–20 sample entry packets (ASNs, Invoices, and 7501s). We will run a
              confidential diagnostic audit to quantify your recoverable capital and identify
              compliance gaps.
            </Typography>
            <Button
              variant="contained"
              className="rd-cta__btn"
              component="a"
              href="https://scheduler.zoom.us/rajeev-malhotra/30-min"
              target="_blank"
              rel="noopener noreferrer"
            >
              Book Your Proof of Value Readout
            </Button>
          </Box>
        </Container>
      </Box>

      {/* ── Custom Footer ─────────────────────────────────────────────────── */}
      <Box component="footer" className="rd-footer">
        <Typography className="rd-footer__text">
          &copy; 2026 LusidPay Inc. All rights reserved. Technology Infrastructure Vendor.
        </Typography>
      </Box>

    </Box>
  );
};

export default RecoveryAndDefensePage;
